// components/EventRegistrationForm/IndividualForm.js
import React, { useRef, useState, useEffect } from 'react';
import { Phone, Mail, User, UserCheck, Check, Info, Loader2, Key, CheckSquare, Users, Building, MapPin, Hash, Camera, X, Search, ChevronDown } from 'lucide-react';
import Swal from 'sweetalert2';

const IndividualForm = ({
    formData,
    hasSubEvents,
    subEvents,
    honorificsOptions,
    loadingSubEvents,
    selectedSubEvents,
    handleInputChange,
    handleVerifyContact,
    participantTypes,
    getFieldDisabledStatus,
    verificationInProgress,
    otpSending,
    phoneInputRef,
    emailInputRef,
    setSelectedSubEvents,
    handleSubEventSelection,
    countries
}) => {
    // Local state
    const [photoPreview, setPhotoPreview] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [photoError, setPhotoError] = useState('');
    const [fetchingPincode, setFetchingPincode] = useState(false);
    const [searchParticipant, setSearchParticipant] = useState('');
    const [showParticipantDropdown, setShowParticipantDropdown] = useState(false);
    const fileInputRef = useRef(null);
    const participantDropdownRef = useRef(null);
    const [showMultiSelectDropdown, setShowMultiSelectDropdown] = useState(false);
    const multiSelectRef = useRef(null);
    const [countryCode, setCountryCode] = useState("+91");

    // Define gender options
    const genderOptions = [
        { value: 'M', label: 'Male' },
        { value: 'F', label: 'Female' },
        { value: 'O', label: 'Other' }
    ];

    // Update total amount when selected sub-events change
    useEffect(() => {
        if (hasSubEvents && subEvents.length > 0 && selectedSubEvents) {
            const totalAmount = selectedSubEvents.reduce((sum, subEventId) => {
                const subEvent = subEvents.find(s => s.SubEvent_Id === subEventId);
                return sum + (subEvent?.TotAmt || 0);
            }, 0);

            if (formData.amount !== totalAmount) {
                handleInputChange({
                    target: {
                        name: 'amount',
                        value: totalAmount
                    }
                });
            }
        }
    }, [selectedSubEvents, subEvents, hasSubEvents, formData.amount, handleInputChange]);

    // Filter participants based on search
    const filteredParticipants = participantTypes.filter(participant =>
        participant.Participant_Name.toLowerCase().includes(searchParticipant.toLowerCase())
    );

    const paidParticipantTypes = new Set(
        (formData.eventDetails || [])
          .filter(e => e.Paid_status === 1)
          .map(e => Number(e.Participant_type))
      );

    const hasAnyPaidSubEvent = () => {
        if (!hasSubEvents || !formData.isExistingUser) return false;

        // Check if any sub-event in eventDetails has Paid_status === 1
        return formData.eventDetails?.some(detail => detail.Paid_status === 1) || false;
    };
    // Update the availableParticipants filtering logic
    // const availableParticipants = participantTypes.filter(participant => {
    //     // If user has already paid for any sub-event, only show the current participant type
    //     if (hasAnyPaidSubEvent()) {
    //         return parseInt(participant.Participant_ID) === parseInt(formData.participantId);
    //     }

    //     // If user has already paid for a specific participant type (non-sub-event)
    //     if (formData.isExistingUser && formData.isAlreadyPaid && formData.paidParticipantTypeId) {
    //         // Only show the participant type they already paid for
    //         return parseInt(participant.Participant_ID) === parseInt(formData.paidParticipantTypeId);
    //     }

    //     // For sub-events with paid status
    //     if (hasSubEvents && formData.isExistingUser && formData.paidSubEvents && formData.paidSubEvents.length > 0) {
    //         // Check if this participant type has any paid sub-events
    //         const hasPaidSubEventsForThisType = formData.eventDetails?.some(
    //             detail => detail.Participant_type === participant.Participant_ID &&
    //                 detail.Paid_status === 1
    //         );

    //         // If this participant type has paid sub-events, only show it
    //         if (hasPaidSubEventsForThisType) {
    //             return true;
    //         }

    //         // Check if all sub-events are paid
    //         if (formData.allSubEventsPaid) {
    //             return parseInt(participant.Participant_ID) === parseInt(formData.participantId);
    //         }
    //     }

    //     // For existing users without payment, show all
    //     return true;
    // });
    const availableParticipants = participantTypes;


    // For dropdown display, use availableParticipants instead of filteredParticipants
    const displayParticipants = searchParticipant
        ? availableParticipants.filter(p =>
            p.Participant_Name.toLowerCase().includes(searchParticipant.toLowerCase())
        )
        : availableParticipants;

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (participantDropdownRef.current && !participantDropdownRef.current.contains(event.target)) {
                setShowParticipantDropdown(false);
            }
            if (multiSelectRef.current && !multiSelectRef.current.contains(event.target)) {
                setShowMultiSelectDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Check if participant type should be disabled
    const isParticipantTypeDisabled = (participantId, memberData = null) => {
        const data = memberData || formData;

        // For sub-events, only disable if ALL sub-events are paid
        if (hasSubEvents) {
            // If all sub-events are paid, there's nothing left to register for
            return data.allSubEventsPaid === true;
        }

        // For non-sub-events (regular events), check if main registration is paid
        if (!data.isExistingUser) return false;

        const participantIdNum = parseInt(participantId);

        // Only disable if this specific participant type's main registration is paid
        return data.isMainRegistrationPaid === true;
    };

    // Fetch pincode details when pincode changes
    useEffect(() => {
        const fetchPincodeDetails = async () => {
            if (!formData.PinCode || formData.PinCode.length !== 6) {
                return;
            }

            setFetchingPincode(true);
            try {
                const response = await fetch(`https://api.postalpincode.in/pincode/${formData.PinCode}`);
                const data = await response.json();

                if (data && data[0] && data[0].Status === "Success") {
                    const postOffice = data[0].PostOffice[0];

                    handleInputChange({
                        target: {
                            name: 'State',
                            value: postOffice.State
                        }
                    });

                    handleInputChange({
                        target: {
                            name: 'District',
                            value: postOffice.District
                        }
                    });

                    setTimeout(() => {
                        Swal.fire({
                            title: 'Location Found!',
                            text: `State: ${postOffice.State}, District: ${postOffice.District}`,
                            icon: 'success',
                            timer: 2000,
                            showConfirmButton: false,
                            background: '#f8fafc',
                            color: '#1f2937'
                        });
                    }, 100);
                } else {
                    Swal.fire({
                        title: 'Invalid Pincode',
                        text: 'Could not find location for this pincode. Please enter manually.',
                        icon: 'error',
                        confirmButtonText: 'OK',
                        background: '#f8fafc',
                        color: '#1f2937'
                    });
                }
            } catch (error) {
                console.error('Error fetching pincode details:', error);
                Swal.fire({
                    title: 'Error',
                    text: 'Failed to fetch location details. Please enter manually.',
                    icon: 'error',
                    confirmButtonText: 'OK',
                    background: '#f8fafc',
                    color: '#1f2937'
                });
            } finally {
                setFetchingPincode(false);
            }
        };

        if (formData.PinCode?.length === 6) {
            fetchPincodeDetails();
        }
    }, [formData.PinCode]);

    // Handle file selection
    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        setPhotoError('');

        if (file) {
            if (!file.type.startsWith('image/')) {
                setPhotoError('Please select a valid image file');
                return;
            }
            if (file.size > 512 * 1024) {
                setPhotoError('File size should be less than 512 KB');
                return;
            }

            const syntheticEvent = {
                target: {
                    name: 'photoFile',
                    files: [file]
                }
            };
            handleInputChange(syntheticEvent);

            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                handleInputChange({
                    target: {
                        name: 'photoPreview',
                        value: reader.result
                    }
                });
            };
            reader.readAsDataURL(file);
        }
    };

    // Function to check individual field status
    const getFieldStatus = (fieldName) => {
        const value = formData[fieldName];
        return value && value.toString().trim() !== '';
    };

    // Handle participant selection
    const handleParticipantSelect = (participant) => {
        // Check if this specific participant type is already paid
        const isParticipantTypePaid = () => {
            if (!formData.isExistingUser || !formData.isAlreadyPaid) return false;

            // For sub-events
            if (hasSubEvents) {
                // Check if ALL sub-events for this participant type are paid
                // Or check if this specific participant type's main registration is paid
                return formData.allSubEventsPaid === true;
            }

            // For regular events - check if the selected participant type matches the paid one
            if (formData.paidParticipantTypeId &&
                parseInt(participant.Participant_ID) === parseInt(formData.paidParticipantTypeId)) {
                return true;
            }

            // For sub-events, also check if this participant type has any paid sub-events
            if (hasSubEvents && formData.paidSubEvents && formData.paidSubEvents.length > 0) {
                // If user has paid for any sub-events with this participant type, block selection
                const hasPaidSubEventsForThisType = formData.eventDetails?.some(
                    detail => detail.Participant_type === participant.Participant_ID &&
                        detail.Paid_status === 1
                );
                if (hasPaidSubEventsForThisType) return true;
            }

            return false;
        };

        const isPaid = isParticipantTypePaid();

        // Add this function near the top of IndividualForm component (after the existing useEffect hooks)


        if (isPaid) {
            Swal.fire({
                title: 'Cannot Change Participant Type',
                text: `You have already registered and paid as "${participant.Participant_Name}". To participate as a different type, please contact the event organizer.`,
                icon: 'warning',
                confirmButtonText: 'OK',
                background: '#f8fafc',
                color: '#1f2937'
            });
            return;
        }

        // Check if user is trying to change from an existing participant type
        if (formData.isExistingUser && formData.participantId &&
            parseInt(formData.participantId) !== parseInt(participant.Participant_ID)) {

            // Show warning that they're changing their participant type
            Swal.fire({
                title: 'Change Participant Type?',
                text: `You are currently registered as "${formData.role}". Changing to "${participant.Participant_Name}" will update your registration. Continue?`,
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Yes, change',
                cancelButtonText: 'Cancel',
                background: '#f8fafc',
                color: '#1f2937'
            }).then((result) => {
                if (result.isConfirmed) {
                    performParticipantSelection(participant);
                }
            });
        } else {
            performParticipantSelection(participant);
        }
    };

    // Helper function to actually perform the participant selection
    const performParticipantSelection = (participant) => {
        handleInputChange({
            target: {
                name: 'role',
                value: participant.Participant_Name
            }
        });

        handleInputChange({
            target: {
                name: 'participantId',
                value: participant.Participant_ID
            }
        });

        setSearchParticipant('');
        setShowParticipantDropdown(false);

        // Clear any previous sub-event selections
        if (hasSubEvents) {
            setSelectedSubEvents([]);
        }
    };

    // Handle participant type search input change
    const handleSearchChange = (e) => {
        setSearchParticipant(e.target.value);
        setShowParticipantDropdown(true);
    };

    // Handle sub-event toggle with slot check
    const handleSubEventToggle = async (subEventId, isChecked, event) => {
        // Stop propagation to prevent dropdown from closing
        if (event) {
            event.stopPropagation();
        }

        // Call the parent's handleSubEventSelection function
        if (handleSubEventSelection) {
            await handleSubEventSelection(subEventId, isChecked);
        }
    };

    return (
        <div className="space-y-6">
            {/* Validation Status Banner */}
            {((formData.phoneVerified && formData.emailVerified) || formData.isExistingUser) && (
                <div className="sticky rounded-lg p-4 transition-all duration-300 bg-yellow-50 border border-yellow-200">
                    <div className="flex items-start">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center mr-3 bg-yellow-100 text-yellow-600">
                            <Info size={18} />
                        </div>
                        <div className="flex-1">
                            <h4 className="font-semibold text-yellow-800">
                                Complete Required Fields
                            </h4>
                            <p className="text-sm mt-1 text-yellow-700">
                                Fill all required fields to enable submission. <span className="text-red-600">* indicates mandatory fields</span>
                            </p>

                            {/* Field completion status */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3 text-xs">
                                {['fullName', 'Ins_Name', 'role', 'gender', 'Ins_Address', 'PinCode', 'Prefix'].map((field) => (
                                    <div key={field} className={`flex items-center ${getFieldStatus(field) ? 'text-green-600' : 'text-red-500'}`}>
                                        <span className="mr-1">{getFieldStatus(field) ? '✓' : '○'}</span>
                                        {field === 'fullName' && 'Full Name *'}
                                        {field === 'Ins_Name' && 'Institution *'}
                                        {field === 'role' && 'Participant Type *'}
                                        {field === 'gender' && 'Gender *'}
                                        {field === 'Ins_Address' && 'Address *'}
                                        {field === 'PinCode' && 'Pincode *'}
                                        {field === 'Prefix' && 'Prefix *'}
                                    </div>
                                ))}
                                <div className={`flex items-center ${formData.photoFile || formData.photoPreview ? 'text-green-600' : 'text-red-500'}`}>
                                    <span className="mr-1">{formData.photoFile || formData.photoPreview ? '✓' : '○'}</span>
                                    Photo *
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Personal Information */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
                <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center">
                    <User className="w-5 h-5 mr-2 text-blue-600" />
                    Personal Information <span className='text-xs text-blue-700'> (Click profile image to upload - Max Size 512KB)</span><span className='text-red-700'>*</span>
                </h3>

                <div className='flex flex-col lg:flex-row gap-6'>
                    {/* Profile Photo Upload Section */}
                    <div className="mb-6">
                        <div className="relative w-fit">
                            <label
                                htmlFor="profile-photo"
                                className={`block cursor-pointer group ${getFieldDisabledStatus('photo') ? 'cursor-not-allowed' : ''}`}
                            >
                                <div
                                    className={`border-2 border-spacing-4 rounded-full p-2 transition-all duration-300
                                        ${getFieldDisabledStatus('photo')
                                            ? 'border-gray-300 bg-gray-100'
                                            : 'border-gray-300 hover:border-blue-400 hover:shadow-lg'
                                        }`}
                                >
                                    <div className="flex items-center justify-center">
                                        <div
                                            className={`relative rounded-full bg-gradient-to-br from-gray-50 to-gray-100 border transition-all duration-300
                                                ${getFieldDisabledStatus('photo')
                                                    ? 'border-gray-200'
                                                    : 'border-gray-200 group-hover:border-blue-200'
                                                }`}
                                        >
                                            {formData.photoPreview ? (
                                                <div className="relative">
                                                    <img
                                                        src={formData.photoPreview}
                                                        alt="Profile preview"
                                                        className="w-44 h-36 object-cover rounded-full shadow-md ring-4 ring-white"
                                                    />

                                                    {!getFieldDisabledStatus('photo') && (
                                                        <>
                                                            <div className="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/20 transition-all duration-300" />
                                                            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                                                <div className="bg-blue-600 text-white px-4 py-2 rounded-full text-xs font-semibold shadow-lg">
                                                                    Change Photo
                                                                </div>
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="flex flex-col items-center justify-center w-36 h-36 rounded-full bg-gradient-to-br from-blue-50 to-indigo-50">
                                                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-3 shadow-sm">
                                                        <Camera size={26} className="text-blue-600" />
                                                    </div>
                                                    <p className="text-sm font-medium text-gray-700">
                                                        Upload Photo *
                                                    </p>
                                                    <p className="text-xs text-gray-500 mt-1">
                                                        JPG, PNG, WEBP
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <input
                                    id="profile-photo"
                                    ref={fileInputRef}
                                    type="file"
                                    className="hidden"
                                    accept="image/*"
                                    onChange={handleFileSelect}
                                />
                            </label>
                            {photoError && (
                                <div className="mt-5 bg-red-50 border border-red-200 rounded-2xl p-4">
                                    <div className="flex items-start">
                                        <div className="w-6 h-6 bg-red-100 text-red-600 rounded-full flex items-center justify-center text-xs font-bold mr-3">
                                            !
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-sm font-semibold text-red-800">
                                                Upload Error
                                            </p>
                                            <p className="text-xs text-red-700 mt-1">{photoError}</p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setPhotoError('')}
                                            className="text-red-500 hover:text-red-700"
                                        >
                                            <X size={16} />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Contact Information Section */}
                    <div className="bg-white w-full rounded-lg p-4 border border-gray-200 mb-6">
                        <h4 className="text-base font-semibold text-gray-900 mb-4 flex items-center">
                            <Phone className="w-4 h-4 mr-2 text-blue-600" />
                            Contact Information <span className="text-red-600 text-sm ml-2">*</span>
                        </h4>

                        <div className="grid md:grid-cols-2 gap-4">
                            {/* Phone Number */}
                            <div className="space-y-2">
                                <label className="text-xs font-semibold text-gray-700 mb-1 flex items-center">
                                    <Phone className="w-3 h-3 mr-1 text-blue-600" />
                                    Phone Number <span className="text-red-600">*</span>
                                </label>

                                <div className="relative">
                                    <div
                                        className={`flex border rounded-lg overflow-hidden transition-all duration-200 ${formData.phoneVerified
                                            ? "border-green-500 bg-green-50"
                                            : "border-gray-300 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-200"
                                            }`}
                                    >
                                        {/* Country Code Dropdown */}
                                        <select
                                            value={countryCode}
                                            onChange={(e) => {
                                                setCountryCode(e.target.value);
                                                // Clear phone when country changes to avoid validation issues
                                                if (formData.phone) {
                                                    handleInputChange({
                                                        target: { name: 'phone', value: '' }
                                                    });
                                                }
                                            }}
                                            disabled={formData.phoneVerified || formData.isExistingUser}
                                            className="px-3 py-2 text-sm bg-gray-100 border-r outline-none min-w-[100px]"
                                        >
                                            <option value="">Select Code</option>
                                            {countries.map((country) => (
                                                <option
                                                    key={`${country.code}-${country.dialCode}`}
                                                    value={country.dialCode}
                                                >
                                                    {country.code} {country.dialCode}
                                                </option>
                                            ))}
                                        </select>

                                        {/* Phone Input */}
                                        <input
                                            ref={phoneInputRef}
                                            type="tel"
                                            name="phone"
                                            value={formData.phone || ""}
                                            onChange={(e) => {
                                                let value = e.target.value.replace(/\D/g, '');

                                                // For Indian numbers (+91), only allow 10 digits
                                                if (countryCode === '+91') {
                                                    if (value.length <= 10) {
                                                        handleInputChange({
                                                            target: { name: 'phone', value: value }
                                                        });
                                                    }
                                                } else {
                                                    // For international numbers, allow up to 15 digits
                                                    if (value.length <= 15) {
                                                        handleInputChange({
                                                            target: { name: 'phone', value: value }
                                                        });
                                                    }
                                                }
                                            }}
                                            required
                                            autoComplete="off"
                                            disabled={formData.phoneVerified || formData.isExistingUser}
                                            className="flex-1 px-3 py-2 text-sm outline-none bg-transparent"
                                            placeholder={countryCode === '+91' ? "Enter 10-digit mobile number" : "Enter phone number"}
                                        />
                                    </div>

                                    {verificationInProgress && (
                                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                            <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
                                        </div>
                                    )}
                                </div>

                                <div className="flex items-center justify-between mt-2">
                                    <p className="text-xs text-gray-500">
                                        {formData.phoneVerified ? (
                                            <span className="flex items-center text-green-600">
                                                <CheckSquare className="w-3 h-3 mr-1" />
                                                {formData.isExistingUser ? "Auto-verified" : "Verified"}
                                            </span>
                                        ) : (
                                            <span>
                                                {countryCode === '+91'
                                                    ? `Enter 10-digit number for ${countryCode}`
                                                    : `Number will be verified with ${countryCode}`}
                                            </span>
                                        )}
                                    </p>

                                    {formData.phone &&
                                        !formData.phoneVerified &&
                                        !formData.isExistingUser && (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    // For Indian numbers, validate 10 digits before sending OTP
                                                    if (countryCode === '+91') {
                                                        if (formData.phone.length !== 10) {
                                                            Swal.fire({
                                                                title: 'Invalid Phone Number',
                                                                text: 'Please enter a valid 10-digit mobile number',
                                                                icon: 'warning',
                                                                confirmButtonText: 'OK'
                                                            });
                                                            return;
                                                        }
                                                    }
                                                    // Pass the full number with country code
                                                    const fullNumber = `${countryCode} ${formData.phone}`;
                                                    handleVerifyContact("mobile", fullNumber);
                                                }}
                                                disabled={otpSending || (countryCode === '+91' && formData.phone.length !== 10)}
                                                className="px-3 py-1 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {otpSending ? (
                                                    <>
                                                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                                        Sending...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Key className="w-3 h-3 mr-1" />
                                                        Verify
                                                    </>
                                                )}
                                            </button>
                                        )}
                                </div>
                            </div>

                            {/* Email Address */}
                            <div className="space-y-2">
                                <label className="text-xs font-semibold text-gray-700 mb-1 flex items-center">
                                    <Mail className="w-3 h-3 mr-1 text-blue-600" />
                                    Email Address <span className="text-red-600">*</span>
                                </label>
                                <div className="relative">
                                    <input
                                        ref={emailInputRef}
                                        type="email"
                                        name="email"
                                        value={formData.email || ''}
                                        onChange={handleInputChange}
                                        required
                                        autoComplete='off'
                                        disabled={getFieldDisabledStatus('email') || formData.emailVerified || formData.isExistingUser}
                                        className={`w-full px-3 py-2 text-sm border outline-none rounded-lg focus:ring-2 transition-all duration-200 ${formData.emailVerified ? 'border-green-500 bg-green-50' : getFieldDisabledStatus('email') ? 'border-gray-300 bg-gray-100' : 'border-gray-300 focus:border-blue-500 focus:ring-blue-200'}`}
                                        placeholder="Enter your email *"
                                    />
                                    {getFieldDisabledStatus('email') && !formData.isExistingUser && (
                                        <div className="absolute inset-0 bg-gray-100 bg-opacity-50 rounded-lg flex items-center justify-center">
                                            <span className="text-xs text-gray-500">Verify phone first</span>
                                        </div>
                                    )}
                                </div>
                                <div className="flex items-center justify-between mt-2">
                                    <p className="text-xs text-gray-500">
                                        {formData.emailVerified ? (
                                            <span className="flex items-center text-green-600">
                                                <CheckSquare className="w-3 h-3 mr-1" />
                                                {formData.isExistingUser ? 'Auto-verified' : 'Verified'}
                                            </span>
                                        ) : (
                                            formData.phoneVerified || formData.isExistingUser ? 'Enter email to verify *' : 'Verify phone first'
                                        )}
                                    </p>
                                    {formData.email && !formData.emailVerified && (formData.phoneVerified || formData.isExistingUser) && !formData.isExistingUser && (
                                        <button
                                            type="button"
                                            onClick={() => handleVerifyContact('email', formData.email)}
                                            disabled={otpSending}
                                            className="px-3 py-1 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200 flex items-center"
                                        >
                                            {otpSending ? (
                                                <>
                                                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                                    Sending...
                                                </>
                                            ) : (
                                                <>
                                                    <Key className="w-3 h-3 mr-1" />
                                                    Verify
                                                </>
                                            )}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Personal Details Section */}
                <div className="bg-white rounded-lg p-4 md:p-6 border border-gray-200 shadow-sm">
                    <h4 className="text-base font-semibold text-gray-900 mb-4 md:mb-6 flex items-center">
                        <User className="w-4 h-4 mr-2 text-blue-600 flex-shrink-0" />
                        Personal Details <span className="text-red-600 text-sm ml-2">* </span>
                    </h4>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                        {/* Prefix and Full Name */}
                        <div className="space-y-2">
                            <label className="text-xs font-medium text-gray-700 mb-1 flex items-center">
                                <Users className="w-3 h-3 mr-1.5 text-blue-600 flex-shrink-0" />
                                Prefix <span className="text-red-600">*</span>
                            </label>
                            <div className="flex flex-col sm:flex-row gap-2 sm:gap-0">
                                <select
                                    name="Prefix"
                                    value={formData.Prefix || ''}
                                    onChange={handleInputChange}
                                    required
                                    disabled={getFieldDisabledStatus('Prefix')}
                                    className="w-full sm:w-1/4 px-3 py-2.5 text-sm border border-gray-300 rounded-lg sm:rounded-r-none sm:rounded-l-lg outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 disabled:bg-gray-50 disabled:cursor-not-allowed"
                                >
                                    <option value="">Prefix *</option>
                                    {honorificsOptions.map((honorific) => (
                                        <option key={honorific.PrefixID} value={honorific.PrefixID}>
                                            {honorific.PrefixName}
                                        </option>
                                    ))}
                                </select>
                                <input
                                    type="text"
                                    name="fullName"
                                    value={formData.fullName || ''}
                                    onChange={handleInputChange}
                                    required
                                    autoComplete="off"
                                    disabled={getFieldDisabledStatus('fullName')}
                                    className={`w-full sm:flex-1 px-3 py-2.5 text-sm border outline-none rounded-lg sm:rounded-l-none sm:rounded-r-lg focus:ring-2 transition-all duration-200 ${getFieldDisabledStatus('fullName')
                                        ? 'border-gray-300 bg-gray-50 cursor-not-allowed'
                                        : 'border-gray-300 focus:border-blue-500 focus:ring-blue-200'
                                        }`}
                                    placeholder="Enter your full name *"
                                />
                            </div>
                            {getFieldDisabledStatus('fullName') && (
                                <p className="text-xs text-gray-500 flex items-center mt-1">
                                    <Loader2 className="w-3 h-3 mr-1.5 animate-spin flex-shrink-0" />
                                    Verify phone and email first
                                </p>
                            )}
                        </div>

                        {/* Gender Field */}
                        <div className="space-y-2">
                            <label className="text-xs font-medium text-gray-700 mb-1 flex items-center">
                                <Users className="w-3 h-3 mr-1.5 text-green-600 flex-shrink-0" />
                                Gender <span className="text-red-600">*</span>
                            </label>
                            <select
                                name="gender"
                                value={formData.gender || ''}
                                onChange={handleInputChange}
                                required
                                disabled={getFieldDisabledStatus('gender')}
                                className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg outline-none focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-all duration-200 disabled:bg-gray-50 disabled:cursor-not-allowed"
                            >
                                <option value="">Select gender *</option>
                                {genderOptions.map((gender) => (
                                    <option key={gender.value} value={gender.value}>
                                        {gender.label}
                                    </option>
                                ))}
                            </select>
                            {getFieldDisabledStatus('gender') && (
                                <p className="text-xs text-gray-500 flex items-center mt-1">
                                    <Loader2 className="w-3 h-3 mr-1.5 animate-spin flex-shrink-0" />
                                    Verify phone and email first
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Professional Information - Only show after verification */}
            {(formData.phoneVerified && formData.emailVerified) || formData.isExistingUser ? (
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-6 border border-green-200">
                    <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center">
                        <UserCheck className="w-5 h-5 mr-2 text-green-600" />
                        Professional Information <span className="text-red-600 text-sm ml-2">* </span>
                    </h3>

                    <div className="bg-white rounded-lg p-4 border border-gray-200 mb-6">
                        <h4 className="text-base font-semibold text-gray-900 mb-4 flex items-center">
                            <UserCheck className="w-4 h-4 mr-2 text-green-600" />
                            Professional Details
                        </h4>

                        <div className="grid md:grid-cols-2 gap-4">
                            {/* Participant Type with Search - Updated to hide other types */}
                            <div className="space-y-2" ref={participantDropdownRef}>
                                <label className="text-xs font-semibold text-gray-700 mb-1 flex items-center">
                                    <UserCheck className="w-3 h-3 mr-1 text-green-600" />
                                    Participant Type <span className="text-red-600">*</span> 
                                    {hasAnyPaidSubEvent() && (
                                        <span className="ml-2 text-xs text-orange-600 font-normal">
                                            (Locked - You have already paid for sub-events)
                                        </span>
                                    )}
                                </label>

                                <div className="relative">
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                        <input
                                            type="text"
                                            value={searchParticipant || formData.role || ''}
                                            onChange={handleSearchChange}
                                            onClick={() => {
                                                // Only show dropdown if not locked due to paid sub-events
                                                // if (!hasAnyPaidSubEvent() && !(formData.isExistingUser && formData.isAlreadyPaid)) {
                                                    setShowParticipantDropdown(true);
                                                // }
                                            }}
                                            placeholder={hasAnyPaidSubEvent() ?
                                                "Participant type locked - Sub-events already paid" :
                                                (formData.isExistingUser && formData.isAlreadyPaid ?
                                                    `Registered as: ${formData.role}` :
                                                    "Search participant type *")}
                                           disabled={getFieldDisabledStatus('role')}

                                            className={`w-full pl-10 pr-3 py-2.5 text-sm border outline-none rounded-lg focus:ring-2 transition-all duration-200 
                    ${(getFieldDisabledStatus('role') 
                        // ||
                        //                             (formData.isExistingUser && formData.isAlreadyPaid) ||
                                                    // hasAnyPaidSubEvent()
                                                    ) 
                                                    ? 'bg-gray-100 cursor-not-allowed border-gray-300'
                                                    : 'bg-white border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200'
                                                }
                                                `}
                                                readOnly={false}  // Add readOnly condition
                                        />
                                        {/* Clear button logic remains the same */}
                                        {formData.role && !(formData.isExistingUser && formData.isAlreadyPaid) && !hasAnyPaidSubEvent() && (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    handleInputChange({
                                                        target: {
                                                            name: 'role',
                                                            value: ''
                                                        }
                                                    });
                                                    handleInputChange({
                                                        target: {
                                                            name: 'participantId',
                                                            value: ''
                                                        }
                                                    });
                                                    setSearchParticipant('');
                                                }}
                                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        )}
                                    </div>

                                    {/* Dropdown remains but hidden when locked */}
                                    {/* {showParticipantDropdown && displayParticipants.length > 0 &&
                                        !(formData.isExistingUser && formData.isAlreadyPaid) &&
                                        !hasAnyPaidSubEvent() && (  // Add this condition */}
                                        {showParticipantDropdown &&
 displayParticipants.length > 0 && (
                                            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                                {/* Dropdown content remains the same */}
                                                {displayParticipants.map((participant) => {
                                                   const isSelected =
                                                   Number(formData.participantId) === Number(participant.Participant_ID);
                                               
                                               const isLocked =
                                                   paidParticipantTypes.has(Number(participant.Participant_ID));
                                                    return (
                                                        <div
                                                            key={participant.Participant_ID}
                                                            onClick={() => {
                                                                if (!isLocked) {
                                                                    handleParticipantSelect(participant);
                                                                }
                                                            }}
                                                            className={`px-4 py-3 transition-colors
                                                                ${
                                                                    isLocked
                                                                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                                                        : "cursor-pointer hover:bg-gray-50"
                                                                }
                                                                ${isSelected ? "bg-blue-50 text-blue-700" : ""}
                                                            `}
                                                        >
                                                            {/* Content remains the same */}
                                                            <div className="flex flex-col">
                                                            <div className="flex justify-between items-center">
    <span>{participant.Participant_Name}</span>

    {isLocked && (
        <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">
            Paid
        </span>
    )}

    {!isLocked && isSelected && (
        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
            Selected
        </span>
    )}
</div>
                                                                {/* {isLocked && (
                                                                    <div className="text-xs text-green-600 mt-1">
                                                                        You are already registered as {participant.Participant_Name}
                                                                    </div>
                                                                )}
                                                                {isSelected && !isLocked && (
                                                                    <div className="text-xs text-green-600 mt-1">
                                                                        ✓ Currently Selected
                                                                    </div>
                                                                )} */}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}

                                    {/* Show locked message when sub-events are paid */}
                                    {hasAnyPaidSubEvent() && formData.role && (
                                        <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded-lg">
                                            <div className="flex items-center gap-2">
                                                <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                                <span className="text-sm text-orange-800">
                                                    Participant type cannot be changed because you have already paid for sub-events.
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-xs font-semibold text-gray-700 mb-1 flex items-center">
                                    <Building className="w-3 h-3 mr-1 text-blue-600" />
                                    Institute / Organization Name <span className="text-red-600">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="Ins_Name"
                                    value={formData.Ins_Name || ''}
                                    onChange={handleInputChange}
                                    required
                                    autoComplete='off'
                                    disabled={getFieldDisabledStatus('Ins_Name')}
                                    className={`w-full px-3 py-2 text-sm border outline-none rounded-lg focus:ring-2 transition-all duration-200 ${getFieldDisabledStatus('Ins_Name') ? 'border-gray-300 bg-gray-100 cursor-not-allowed' : 'border-gray-300 focus:border-blue-500 focus:ring-blue-200'}`}
                                    placeholder="Enter institution name *"
                                />
                                {getFieldDisabledStatus('Ins_Name') && (
                                    <p className="text-xs text-gray-500 mt-1">
                                        <span className="flex items-center">
                                            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                            Verify phone and email first
                                        </span>
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Institution Address Section */}
                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                        <h4 className="text-base font-semibold text-gray-900 mb-4 flex items-center">
                            <Building className="w-4 h-4 mr-2 text-green-600" />
                            Address Details <span className="text-red-600 text-sm ml-2">* </span>
                        </h4>

                        <div className="grid md:grid-cols-2 gap-4">
                            {/* Institution Address */}
                            <div className="space-y-2">
                                <label className="text-xs font-semibold text-gray-700 mb-1 flex items-center">
                                    <MapPin className="w-3 h-3 mr-1 text-green-600" />
                                    Address <span className="text-red-600">*</span>
                                </label>
                                <textarea
                                    name="Ins_Address"
                                    value={formData.Ins_Address || ''}
                                    onChange={handleInputChange}
                                    rows="3"
                                    required
                                    autoComplete='off'
                                    disabled={getFieldDisabledStatus('Ins_Address')}
                                    className={`w-full px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-all duration-200 ${getFieldDisabledStatus('Ins_Address') ? 'bg-gray-100' : ''}`}
                                    placeholder="Enter complete institution address *"
                                />
                                {getFieldDisabledStatus('Ins_Address') && (
                                    <p className="text-xs text-gray-500">
                                        Verify phone and email first
                                    </p>
                                )}
                            </div>

                            {/* Pincode */}
                            <div className="space-y-2">
                                <label className="text-xs font-semibold text-gray-700 mb-1 flex items-center">
                                    <Hash className="w-3 h-3 mr-1 text-green-600" />
                                    Pincode <span className="text-red-600">*</span>
                                </label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        name="PinCode"
                                        value={formData.PinCode || ''}
                                        onChange={handleInputChange}
                                        maxLength={6}
                                        required
                                        autoComplete='off'
                                        onKeyDown={(e) => {
                                            if (
                                                !/[0-9]/.test(e.key) &&
                                                e.key !== 'Backspace' &&
                                                e.key !== 'Tab' &&
                                                e.key !== 'ArrowLeft' &&
                                                e.key !== 'ArrowRight'
                                            ) {
                                                e.preventDefault();
                                            }
                                        }}
                                        disabled={getFieldDisabledStatus('PinCode')}
                                        className={`w-full px-3 py-2 text-sm border outline-none rounded-lg focus:ring-2 transition-all duration-200 ${getFieldDisabledStatus('PinCode') ? 'border-gray-300 bg-gray-100 cursor-not-allowed' : 'border-gray-300 focus:border-green-500 focus:ring-green-200'}`}
                                        placeholder="6-digit pincode *"
                                    />
                                    {fetchingPincode && (
                                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                            <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
                                        </div>
                                    )}
                                </div>
                                {getFieldDisabledStatus('PinCode') && (
                                    <p className="text-xs text-gray-500">
                                        Verify phone and email first
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Sub Events Section */}
                    {hasSubEvents && formData.participantId && (
                        <div className="mt-4 bg-white rounded-lg p-4 border border-gray-200">
                            {/* Header with compact total */}
                            <div className="flex items-center justify-between mb-3">
                                <h4 className="text-sm sm:text-base font-semibold text-gray-700 flex items-start sm:items-center">
                                    <UserCheck className="w-4 h-4 mr-1.5 text-purple-600 flex-shrink-0 mt-0.5 sm:mt-0" />
                                    {formData.role ? (
                                        <span className="flex flex-wrap items-baseline">
                                            <span className="font-semibold text-purple-700 whitespace-nowrap">
                                                {formData.role}(s)
                                            </span>
                                            <span>&nbsp;can participate in the following events</span>
                                        </span>
                                    ) : (
                                        <span className="leading-tight">
                                            Select a participant type to view available events
                                        </span>
                                    )}
                                </h4>

                                {selectedSubEvents?.length > 0 && (
                                    <div className="flex items-center bg-purple-50 px-2.5 py-1 rounded-md">
                                        <span className="text-xs text-gray-600 mr-1.5">Total:</span>
                                        <span className="text-sm font-bold text-purple-700">
                                            ₹{selectedSubEvents.reduce((sum, id) => {
                                                const subEvent = subEvents.find(s => s.SubEvent_Id === id);
                                                return sum + (subEvent?.TotAmt || 0);
                                            }, 0).toLocaleString('en-IN')}
                                        </span>
                                    </div>
                                )}
                            </div>

                            <div className="relative" ref={multiSelectRef}>
                                {/* Compact trigger button */}
                                <button
                                    type="button"
                                    onClick={() => setShowMultiSelectDropdown(!showMultiSelectDropdown)}
                                    className={`w-full px-3 py-2 text-sm border rounded-lg flex items-center justify-between bg-white transition ${showMultiSelectDropdown ? 'border-purple-400 ring-2 ring-purple-100' : 'border-gray-300 hover:border-purple-300'
                                        }`}
                                >
                                    <span className="flex items-center truncate">
                                        {selectedSubEvents?.length > 0 ? (
                                            <>
                                                <span className="bg-purple-100 text-purple-700 text-xs px-2 py-0.5 rounded-full mr-2">
                                                    {selectedSubEvents.length}
                                                </span>
                                                <div className="text-gray-600 text-sm flex-1 min-w-0">
                                                    <span className="truncate block">
                                                        {selectedSubEvents.slice(0, 4).map((id, index) => {
                                                            const item = subEvents.find(s => s.SubEvent_Id === id);
                                                            return (
                                                                <span key={id}>
                                                                    {item?.SubEvent_Name}
                                                                    {index < Math.min(selectedSubEvents.length, 4) - 1 && ', '}
                                                                </span>
                                                            );
                                                        })}
                                                    </span>
                                                    {selectedSubEvents.length > 4 && (
                                                        <span className="text-purple-600 text-xs ml-1">
                                                            +{selectedSubEvents.length - 4} more
                                                        </span>
                                                    )}
                                                </div>
                                            </>
                                        ) : (
                                            <span className="text-gray-400">Select sub events</span>
                                        )}
                                    </span>
                                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showMultiSelectDropdown ? 'rotate-180' : ''}`} />
                                </button>

                                {/* Compact dropdown */}
                                {showMultiSelectDropdown && (
                                    <div className="absolute z-30 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                                        {/* Compact search */}
                                        <div className="p-2 border-b bg-gray-50">
                                            <div className="relative">
                                                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                                                <input
                                                    type="text"
                                                    placeholder="Search..."
                                                    className="w-full pl-7 pr-2 py-1.5 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-purple-200 focus:border-purple-400 outline-none"
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                    value={searchTerm || ''}
                                                />
                                            </div>
                                        </div>

                                        {/* Quick actions */}
                                        <div className="flex border-b bg-gray-50 px-2 py-1.5">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    // Only select sub-events that are not paid
                                                    const unpaidSubEvents = subEvents
                                                        .filter(s => {
                                                            // Check if this sub-event is not paid
                                                            const isPaid = formData.eventDetails?.some(
                                                                detail => detail.SubEvent_Id === s.SubEvent_Id && detail.Paid_status === 1
                                                            );
                                                            return !isPaid;
                                                        })
                                                        .map(s => s.SubEvent_Id);

                                                    // Select each unpaid sub-event with slot check
                                                    unpaidSubEvents.forEach(async (subEventId) => {
                                                        await handleSubEventToggle(subEventId, true, null);
                                                    });
                                                }}
                                                className="text-xs text-purple-600 hover:text-purple-800 px-2 py-0.5"
                                            >
                                                All Unpaid
                                            </button>
                                            <span className="text-gray-300 mx-1">|</span>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    // Deselect all sub-events
                                                    selectedSubEvents.forEach(async (subEventId) => {
                                                        await handleSubEventToggle(subEventId, false, null);
                                                    });
                                                }}
                                                className="text-xs text-gray-500 hover:text-gray-700 px-2 py-0.5"
                                            >
                                                Clear
                                            </button>
                                        </div>

                                        {/* Options list */}
                                        <div className="max-h-48 overflow-y-auto">
                                            {loadingSubEvents ? (
                                                <div className="p-3 text-center">
                                                    <Loader2 className="w-4 h-4 text-purple-600 animate-spin mx-auto" />
                                                </div>
                                            ) : subEvents?.filter(option =>
                                                option.SubEvent_Name?.toLowerCase().includes(searchTerm?.toLowerCase() || "")
                                            ).length > 0 ? (
                                                subEvents
                                                    .filter(option =>
                                                        option.SubEvent_Name?.toLowerCase().includes(searchTerm?.toLowerCase() || "")
                                                    )
                                                    .map((option) => {
                                                        const isSelected = selectedSubEvents?.includes(option.SubEvent_Id);
                                                        // Check if this specific sub-event is paid from the eventDetails
                                                        const isPaid = formData.eventDetails?.some(
                                                            detail => detail.SubEvent_Id === option.SubEvent_Id && detail.Paid_status === 1
                                                        );

                                                        return (
                                                            <div
                                                                key={option.SubEvent_Id}
                                                                className={`flex items-center justify-between px-3 py-2 cursor-pointer text-sm 
                                                                    ${isPaid ? 'bg-gray-100 cursor-not-allowed opacity-80' : ''}
                                                                    ${isSelected && !isPaid ? 'bg-purple-50' : 'hover:bg-gray-50'}`}
                                                                onClick={async (e) => {
                                                                    e.stopPropagation();
                                                                    // Only allow selection if sub-event is not paid
                                                                    if (!isPaid) {
                                                                        if (!isSelected) {
                                                                            // Check slot availability before selecting
                                                                            await handleSubEventToggle(option.SubEvent_Id, true, e);
                                                                        } else {
                                                                            await handleSubEventToggle(option.SubEvent_Id, false, e);
                                                                        }
                                                                    }
                                                                }}
                                                            >
                                                                <div className="flex items-center min-w-0 flex-1">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={isSelected}
                                                                        readOnly
                                                                        disabled={isPaid}
                                                                        className={`w-3.5 h-3.5 rounded mr-2 flex-shrink-0 
                                                                            ${isPaid
                                                                                ? 'text-gray-400 cursor-not-allowed'
                                                                                : 'text-purple-600 cursor-pointer'}`}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        onChange={async (e) => {
                                                                            e.stopPropagation();
                                                                            if (!isPaid) {
                                                                                await handleSubEventToggle(option.SubEvent_Id, e.target.checked, e);
                                                                            }
                                                                        }}
                                                                    />
                                                                    <span className="text-xs truncate">{option.SubEvent_Name}</span>
                                                                </div>
                                                                <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                                                                    {isPaid && (
                                                                        <span className="text-[10px] font-medium bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
                                                                            Already Paid
                                                                        </span>
                                                                    )}
                                                                    <span className={`text-xs font-medium ${isPaid ? 'text-gray-400' : 'text-gray-600'} bg-gray-100 px-1.5 py-0.5 rounded`}>
                                                                        ₹{option.TotAmt}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        );
                                                    })
                                            ) : (
                                                <div className="p-4 text-center text-xs text-gray-400">
                                                    No matches found
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Compact selected items chips */}
                            {selectedSubEvents?.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mt-3">
                                    {selectedSubEvents.map((id) => {
                                        const item = subEvents.find(s => s.SubEvent_Id === id);
                                        const isPaid = formData.eventDetails?.some(
                                            detail => detail.SubEvent_Id === id && detail.Paid_status === 1
                                        );

                                        return (
                                            <span
                                                key={id}
                                                className={`inline-flex items-center text-xs px-2 py-1 rounded border
                                                    ${isPaid
                                                        ? 'bg-gray-100 text-gray-500 border-gray-200 cursor-not-allowed'
                                                        : 'bg-purple-50 text-purple-700 border-purple-200'}`}
                                            >
                                                <span className="max-w-[80px] truncate">{item?.SubEvent_Name}</span>
                                                <span className={`ml-1 text-[10px] font-medium px-1 rounded
                                                    ${isPaid ? 'bg-gray-200 text-gray-600' : 'bg-white text-purple-600'}`}>
                                                    ₹{item?.TotAmt}
                                                </span>
                                                {!isPaid && (
                                                    <button
                                                        onClick={async (e) => {
                                                            e.stopPropagation();
                                                            await handleSubEventToggle(id, false, e);
                                                        }}
                                                        className="ml-1 text-purple-400 hover:text-red-500"
                                                    >
                                                        ✕
                                                    </button>
                                                )}
                                                {isPaid && (
                                                    <span className="ml-1 text-green-600">✓</span>
                                                )}
                                            </span>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            ) : null}

            {/* Additional Information - Only show after verification */}
            {(formData.phoneVerified && formData.emailVerified) || formData.isExistingUser ? (
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                        <Info className="w-5 h-5 mr-2 text-blue-600" />
                        Additional Notes (Optional)
                    </h3>
                    <div className="space-y-2">
                        <textarea
                            name="Comments"
                            value={formData.Comments || ''}
                            onChange={handleInputChange}
                            rows="3"
                            autoComplete='off'
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
                            placeholder="Any additional information or special requirements... (Optional)"
                        />
                        <p className="text-xs text-gray-500">
                            Optional: Add any special requirements, dietary restrictions, or other notes
                        </p>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default IndividualForm;